import { EventQueue } from '../eventQueue'
import { Broadcast, broadcast, BroadcastTarget } from '../broadcast'
import TimeoutError from '../TimeoutError'
import UnhandledError from '../UnhandledError'
import { ClientResponse, createResponse, parseData, isBrowser } from './utils'
import { broadcastResponse } from '../broadcast/response'
import { EventOptions } from '../events'

export type ClientType = 'parent' | 'child'

export type EventListener<T> = (data: T, error?: UnhandledError) => void

const DEFAULT_REQUEST_TIMEOUT = 3000
const MAX_IDLE_TIMEOUT = DEFAULT_REQUEST_TIMEOUT * 0.5

export interface ClientConfig<T extends BroadcastTarget = BroadcastTarget> {
  target: T
  childName?: T extends 'child' ? string : never
  trustedOrigins?: string[]
}

export class Client<T extends BroadcastTarget = BroadcastTarget> {
  private config: ClientConfig<T>
  private listeners: Partial<Record<string, EventListener<any>>> = {}
  private responses: Partial<
    Record<string, ClientResponse<string, any, any>>
  > = {}
  private eventQueue: EventQueue

  private handleListener = (
    eventName: string,
    data: unknown,
    error: UnhandledError | undefined,
    options: EventOptions
  ) => {
    const listener = this.listeners[eventName]
    if (!listener) {
      return
    }

    if (!options.handleImmediately) {
      this.eventQueue.push(listener, data, error)
      return
    }

    try {
      listener(data, error)
    } catch (error) {
      console.error(error)
    }
  }

  private handleResponse = (
    eventName: string,
    source: Window | MessagePort | ServiceWorker | null,
    data: unknown
  ) => {
    const response = this.responses[eventName]
    if (!response) {
      return
    }

    try {
      broadcastResponse(source, response(data), this.config.target)
    } catch (error) {
      console.error(error)
    }
  }

  private handleEvent = (
    source: Window | MessagePort | ServiceWorker | null,
    payload: Broadcast<string, any>
  ) => {
    const { eventName, data, options = {}, error } = payload

    if (!eventName) {
      // This is not a pollard event :)
      return
    }

    this.handleResponse(eventName, source, data)
    this.handleListener(eventName, data, error, options)
  }

  private receiveMessage = ({
    origin,
    data,
    source
  }: WindowEventMap['message']) => {
    const { trustedOrigins = [] } = this.config

    if (trustedOrigins.length > 0 && !trustedOrigins.includes(origin)) {
      console.warn(`Untrusted origin: "${origin}". ${JSON.stringify(source)}`)
      return
    }

    const payload = parseData<Broadcast<string>>(data)
    if (!payload) {
      return
    }

    this.handleEvent(source, payload)
  }

  constructor(config: ClientConfig<T>) {
    this.config = config
    this.eventQueue = new EventQueue(MAX_IDLE_TIMEOUT)

    if (isBrowser()) {
      window.addEventListener('message', this.receiveMessage, {
        capture: true,
        passive: true
      })
    }
  }

  listenTo = <E extends string, T>(eventName: E, handler: EventListener<T>) => {
    this.listeners[eventName] = handler
  }

  mute = <E extends string>(eventName: E) => {
    // TODO: Improve method signature -> new major version
    delete this.listeners[eventName]
    delete this.responses[eventName]
  }

  respondTo = <E extends string, T, R>(
    eventName: E,
    response: ClientResponse<E, T, R>
  ) => {
    this.responses[eventName] = createResponse<E, T, R>(
      eventName,
      response,
      this.config.childName
    )
  }

  request = async <E extends string, T, R>(
    { data, eventName, options }: Omit<Broadcast<E, T>, 'error'>,
    timeout = DEFAULT_REQUEST_TIMEOUT
  ): Promise<R> =>
    new Promise((resolve, reject) => {
      const { target, childName } = this.config

      const timeoutHandler = setTimeout(() => {
        reject(new TimeoutError(eventName, timeout, data, options))
      }, timeout)

      this.listenTo<E, R>(eventName, (response, error) => {
        clearTimeout(timeoutHandler)

        if (error) {
          reject(error)
        }

        resolve(response)
      })

      broadcast<E, T>(target, { eventName, data, options, childName })
    })
}
